/**
 * useChatStream — the React hook that owns the chat session state.
 *
 * Responsibilities:
 *   - Keep the list of messages (user + assistant)
 *   - Track in-flight tool calls so the UI can render ToolCallCard
 *   - Stream text into the in-progress assistant message
 *   - Surface the loading flag while a turn is running
 *   - Let the caller cancel an in-flight turn
 *
 * Why a hook and not Redux/Zustand: this is local UI state, one component
 * tree owns it. No persistence, no cross-component sharing.
 */
import { useCallback, useRef, useState } from "react";
import type { ChatStreamEvent, TurnCompleteEvent } from "../lib/events";
import { streamChat } from "../lib/api";

/**
 * One inline tool-call (`query_athena`, etc) shown in the chat thread.
 * Renders as a card with status: "running" while the tool is executing,
 * "ok" or "error" once it finishes.
 */
export interface ToolCall {
  id: string;
  name: string;
  status: "running" | "ok" | "error";
  summary?: string;
  startedAt: number; // ms epoch
  finishedAt?: number;
}

/**
 * One message in the chat thread. User messages are simple text. Assistant
 * messages can contain text AND a list of tool calls (in order they happened).
 */
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  toolCalls: ToolCall[];
  isStreaming: boolean;
  error?: string;
  usage?: TurnCompleteEvent["usage"];
  rounds?: number;
}

export interface UseChatStreamResult {
  messages: ChatMessage[];
  isStreaming: boolean;
  sendMessage: (text: string) => void;
  cancel: () => void;
  reset: () => void;
}

function makeId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function useChatStream(conversationId: string): UseChatStreamResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  // Hold the AbortController so cancel() can interrupt mid-stream.
  const abortRef = useRef<AbortController | null>(null);

  // Index of the assistant message we're currently streaming into.
  const streamingMsgIdRef = useRef<string | null>(null);

  const handleEvent = useCallback((event: ChatStreamEvent) => {
    setMessages((prev) => {
      const next = [...prev];
      const msgId = streamingMsgIdRef.current;
      const msgIdx = msgId ? next.findIndex((m) => m.id === msgId) : -1;
      if (msgIdx === -1) return prev;
      const current = { ...next[msgIdx] };

      switch (event.type) {
        case "text_delta":
          current.text = current.text + event.text;
          break;

        case "tool_call_start":
          current.toolCalls = [
            ...current.toolCalls,
            {
              id: event.id,
              name: event.name,
              status: "running",
              startedAt: Date.now(),
            },
          ];
          break;

        case "tool_call_result": {
          current.toolCalls = current.toolCalls.map((tc) =>
            tc.id === event.id
              ? {
                  ...tc,
                  status: event.ok ? "ok" : "error",
                  summary: event.summary,
                  finishedAt: Date.now(),
                }
              : tc,
          );
          break;
        }

        case "turn_complete":
          current.isStreaming = false;
          current.usage = event.usage;
          current.rounds = event.rounds;
          // If the model returned tool results but produced no final text,
          // surface the final_text field (it may differ from the streamed text).
          if (!current.text && event.final_text) {
            current.text = event.final_text;
          }
          break;

        case "turn_error":
          current.isStreaming = false;
          current.error = event.message;
          break;
      }

      next[msgIdx] = current;
      return next;
    });
  }, []);

  const sendMessage = useCallback(
    (text: string) => {
      if (isStreaming) return; // ignore while a turn is in flight
      if (!text.trim()) return;

      const userMsg: ChatMessage = {
        id: makeId(),
        role: "user",
        text: text.trim(),
        toolCalls: [],
        isStreaming: false,
      };

      const assistantMsg: ChatMessage = {
        id: makeId(),
        role: "assistant",
        text: "",
        toolCalls: [],
        isStreaming: true,
      };

      streamingMsgIdRef.current = assistantMsg.id;

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const abort = new AbortController();
      abortRef.current = abort;

      streamChat(conversationId, text.trim(), {
        onEvent: handleEvent,
        onError: (e) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? { ...m, isStreaming: false, error: e.message }
                : m,
            ),
          );
        },
        onDone: () => {
          setIsStreaming(false);
          streamingMsgIdRef.current = null;
          abortRef.current = null;
        },
        signal: abort.signal,
      });
    },
    [conversationId, handleEvent, isStreaming],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsStreaming(false);
    streamingMsgIdRef.current = null;
  }, []);

  return { messages, isStreaming, sendMessage, cancel, reset };
}
